import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, queryAll, state, query } from 'lit/decorators.js';
import { styles } from './styles';
import { HeaderItemEvents, HeaderMenuItem } from '../types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { classMap } from 'lit/directives/class-map.js';
import { SelectOverlayElement } from '../../gzSelect';
import { isEqual } from 'lodash';
import { repeat } from 'lit/directives/repeat.js';
import { UserRoleWebComponent } from '../ui/userRole';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { Theme } from 'libs/shared/color-theme/src/lib/types';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../utils/request';
/**
 * @summary Type here the description of this component
 * @tag gz-header-ui
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-header-ui')
export class GzHeaderUiWebComponent extends LitElement {
  @property({
    type: Array,
    attribute: true,
    reflect: true,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  items: HeaderMenuItem[] = [];

  @queryAll('.menu-item-button')
  menuItems!: HTMLElement[];

  @query('select-overlay')
  overlay!: SelectOverlayElement;

  @state()
  private activeOverlayButtonId: null | string = null;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const overlay = window.document.body.querySelector('select-overlay') as SelectOverlayElement;
    overlay.opened = false;
    this.activeOverlayButtonId = null;
  }

  render() {
    return this.renderMenuItems();
  }

  private renderMenuItems(): TemplateResult | undefined {
    if (isEmpty(this.items)) {
      return;
    }

    return html`${repeat(this.items, (item: HeaderMenuItem) => {
      if (item.isVisible) {
        return html`${this.renderMenuItem(item)}`;
      }
    })}`;
  }

  private renderMenuItem(item: HeaderMenuItem): TemplateResult {
    const classes = {
      'menu-item-button': true,
      active: item.isActive || item.id === this.activeOverlayButtonId,
    };
    return html` <gz-tooltip class="tooltip" tooltipText="${item.name}">
      <button class="${classMap(classes)}" @click="${e => this.buttonClickHandler(item, e)}" id="${item.id}">
        ${item.badgeContent ? this.renderBadge(item.badgeContent) : nothing}
        <span class="${item.icon} menu-item-icon"></span>
      </button>
      ${item.content ? this.renderItemContent(item.content, item.id) : nothing}
    </gz-tooltip>`;
  }

  private buttonClickHandler(item: HeaderMenuItem, e: Event): void {
    e.stopPropagation();
    if (item.handler) {
      item.handler();
    } else if (item.content) {
      this.dispatchEvent(
        new CustomEvent('menuItemClicked', {
          detail: {
            itemId: item.id,
          },
          bubbles: true,
          composed: true,
        })
      );
      this.openOverlay(item.id);

      this.getSavedTheme().then(savedThemeResponse => {
        if (savedThemeResponse) {
          this.updateActiveThemeButton(savedThemeResponse);
        }
      });

    } else {
      return;
    }
  }


  private async getSavedTheme(): Promise<Theme> {
    const response: { darkModeState: Theme } = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'AdditionalUserSettings',
      method: 'getDarkThemeSettings',
    });
    return response.darkModeState;
  }

  private updateActiveThemeButton(theme: Theme): void {
    const userAccount = window.document.querySelector('user-role') as UserRoleWebComponent;

    if (!userAccount) {
      return;
    }

    userAccount.themeButtons.forEach((button: HTMLElement) => {
      if (button.classList.contains(theme)) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }

  private renderBadge(content: number): TemplateResult | undefined {
    if (content !== 0) {
      return html`<span class="badge">${content}</span>`;
    }
  }

  private renderItemContent(content: TemplateResult, id: string): TemplateResult {
    return html`<select-overlay
      id="overlay-${id}"
      style="width: 240px;"
      class="dark-style"
      @vaadin-overlay-close="${() => this.onOverlayClose(id)}"
      @opened-changed="${() => this.dispatchEvent(new CustomEvent(HeaderItemEvents.MENU_OPEN))}"
      >${content}</select-overlay
    >`;
  }

  public getMenuItem(id: string): HTMLElement | undefined {
    return Array.from(new Set(this.menuItems)).filter((item: HTMLElement) => item.id === id)[0];
  }

  private alignOverlayToTarget(overlay: SelectOverlayElement): void {
    let translateX = 0;
    let translateY = 0;

    const targetRect = this.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    const devicePixelRatio = window.devicePixelRatio || 1;

    translateX = targetRect.left - (overlayRect.width - targetRect.width) + (translateX || 0);

    translateX = Math.round(translateX * devicePixelRatio) / devicePixelRatio;
    translateY = targetRect.top + (translateY || 0) + targetRect.height;
    translateY = Math.round(translateY * devicePixelRatio) / devicePixelRatio;
    overlay.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
  }

  private openOverlay(id: string): void {
    if (this.activeOverlayButtonId === id) {
      this.activeOverlayButtonId = null;
      return;
    }
    const overlay = this.shadowRoot?.getElementById(`overlay-${id}`) as SelectOverlayElement;
    overlay.opened = true;
    overlay.style.opacity = '1';
    this.activeOverlayButtonId = id;
    this.alignOverlayToTarget(overlay);
    this.setContentOverlayLoading();
  }

  private setContentOverlayLoading() {
    const userAccount = window.document.querySelector('user-role') as UserRoleWebComponent;
    if (!userAccount) {
      return;
    }

    userAccount.setLoading(true);
  }

  public clearOverlayButtonId(): void {
    this.activeOverlayButtonId = null;
  }

  private onOverlayClose(id: string): void {
    const menuItem = this.getMenuItem(id) as HTMLElement;
    if (menuItem) {
      menuItem.classList.remove('active');
    }

    const overlay = window.document.querySelector(`select-overlay#overlay-${id}`) as SelectOverlayElement;

    document.addEventListener('click', event => {
      // @ts-ignore
      if (!menuItem.contains(event.target) && !overlay.opened) {
        this.activeOverlayButtonId = null;
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-header-ui': GzHeaderUiWebComponent;
  }
}
